<template>
  <main class="activate">
    <h1 class="activate__title">Задайте пароль</h1>
    <form class="activate__form" v-on:submit.prevent="onSubmit">
      <password-input
        v-model="password"
        label="Пароль"
        v-bind:input-errors="passwordInputErrors"
      />
      <password-input
        v-model="confirmPassword"
        label="Повторите пароль"
        v-bind:input-errors="confirmPasswordInputErrors"
      />
      <password-info />
      <div class="activate__form__row">
        <button
          class="activate__form__button-submit"
          type="submit"
          v-bind:disabled="isAwaitRequest">
          Продолжить
        </button>
        <div class="activate__form__policy">
          Нажимая на кнопку Продолжить, я принимаю
          <router-link target="_blank" v-bind:to="{ name: 'PrivacyPolicy' }">
            положения политики конфиденциальности
          </router-link>
          и даю свое согласие на обработку персональных данных
        </div>
      </div>
    </form>
  </main>
</template>
<script>
import { REGISTRATION_FEATURE, isRemovedFeature } from '@/removableFeatures';
import { validatePassword } from '@/inputConstraint';
import violations from '@/violations.json';
import notifierMixin from '@/mixins/notifierMixin';
import PasswordInput from '@/components/PasswordInput.vue';
import PasswordInfo from '@/components/PasswordInfo.vue';

/**
 * @description Аккаунт уже активирован.
 * @type {string}
 */
const ALREADY_ACTIVATED = 'already_activated';

/**
 * @description Некорректный код активации.
 * @type {string}
 */
const INVALID_CODE = 'invalid_code';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.violationCauses ? vm.violationCauses[cause] : []
);

/**
 * @description Компонент страницы активации аккаунта.
 * @type {Vue}
 */
export default {
  components: { PasswordInput, PasswordInfo },
  computed: {
    confirmPasswordInputErrors: resolveInputErrors('confirm_password'),
    passwordInputErrors: resolveInputErrors('password'),
  },
  data() {
    return {
      /**
       * @description Подтверждение пароля.
       * @type {string}
       */
      confirmPassword: '',
      /**
       * @description Пароль.
       * @type {string}
       */
      password: '',
      /**
       * @description Ожидание завершения запроса.
       * @type {boolean}
       */
      isAwaitRequest: false,
      /**
       * @description Нарушения ограничений.
       * @type {any}
       */
      violationCauses: {},
    };
  },
  methods: {
    /**
     * @description Создать запрос.
     * @returns {any}
     */
    makeRequest() {
      const code = this.$route.query.code || '';
      const request = {
        password: this.password,
        confirm_password: this.confirmPassword,
        code,
      };
      return request;
    },
    /**
     * @description Содержит нарушения.
     * @param {any} request Запрос.
     * @returns {boolean}
     */
    hasViolations(request) {
      const violationCauses = {
        password: [],
        confirm_password: [],
      };
      if (!request.password) {
        violationCauses.password.push(violations.password.empty);
      } else if (!validatePassword(request.password)) {
        violationCauses.password.push(violations.password.format);
      }
      if (!request.confirm_password) {
        violationCauses.confirm_password.push(
          violations.confirm_password.empty,
        );
      } else if (request.password !== request.confirm_password) {
        violationCauses.confirm_password.push(
          violations.confirm_password.not_equals,
        );
      }
      this.violationCauses = violationCauses;
      return Object.values(violationCauses).some((arr) => arr.length !== 0);
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      const request = this.makeRequest();
      if (this.hasViolations(request)) {
        return;
      }
      const onSuccess = ({ message, email }) => {
        this.notifySuccess(`${message}. Сейчас вы будете перенаправлены на страницу входа`);
        const toAuth = () => {
          this.$router.push({ name: 'Auth', params: { initialEmail: email } });
        };
        setTimeout(toAuth, 3000);
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.violationCauses = error.allCauses();
        if (error.hasCauses()) {
          if (error.has(ALREADY_ACTIVATED)) {
            this.$router.push({ name: 'Auth' });
          } else if (error.has(INVALID_CODE)) {
            this.$router.push({
              name: isRemovedFeature(REGISTRATION_FEATURE) ? 'Auth' : 'Registration',
            });
          }
        }
      };
      this.isAwaitRequest = true;
      const userId = this.$route.params.id;
      this.$activateGateway
        .activate(userId, request)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitRequest = false; });
    },
  },
  mixins: [notifierMixin],
  name: 'AccountActivate',
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";
.activate {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  max-width: 589px;
  width: 100%;
  &__title {
    font-style: normal;
    font-weight: 600;
    font-size: 50px;
    line-height: 60px;
    margin: 0 auto 40px 0;
  }
  &__form {
    &__button-submit {
      @include buttons.btn-blue(100%, 70px, 20px);
      &:disabled {
        background-color: var(--unfocus-color);
        border-color: var(--unfocus-color);
      }
    }
    &__policy {
      font-weight: 400;
      font-size: 12px;
      line-height: 14px;
      color: #707683;
      a {
        color: #707683 !important;
        &:hover {
          text-decoration: none;
        }
      }
    }
    &__row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 25px;
      align-items: center;
    }
  }
}
@media screen and (max-width: 1200px) {
  .activate {
    &__title {
      padding: 0 20px;
    }
    &__form {
      margin: 30px auto 0 auto;
      padding: 0 20px 40px;
    }
  }
}
@media screen and (max-width: 480px) {
  .activate {
    &__title {
      font-size: 28px;
      line-height: 30px;
      margin: 0 auto 20px 0;
    }
    &__form {
      &__button-submit {
        margin: 0 0 20px 0;
      }
      &__row {
        display: grid;
        grid-template-columns: 1fr;
        grid-gap: 0;
      }
    }
  }
}
</style>
